import React, { useContext, forwardRef } from 'react';
import { Recording, UserWebAsset } from '~/types/models';
import { TestimonialRespondentsMedia } from '~/types/models/testimonial';
import { UserAsset } from '../Api/UserAsset';
import RecordingListItem from './RecordingListItem';
import RespondentsMediaItem from './RespondentsMediaItem';
import cx from 'classnames';
import { GridTileType } from './InfiniteMediaLibrary';
import InfinityMediaItemConfigContext from '../Context/infinite-media-item-config-context';
import WebAssetThumbnail from './WebAssetThumbnail';
import { SocialType } from '../../Socials/util';
import UserAssetListItem from './UserAssetListItem';

export type MediaLibraryAsset =
  | Recording
  | UserAsset
  | UserWebAsset
  | TestimonialRespondentsMedia;

export interface InfiniteMediaLibraryItemProps {
  asset: MediaLibraryAsset;
  onTagUpdated?: (resourceId?: string) => void;
  gridTileType?: GridTileType;
  onThumbnailLoad?: () => void;
}

const InfiniteMediaLibraryItem = forwardRef<
  HTMLDivElement,
  InfiniteMediaLibraryItemProps
>((props, detailsContainerRef) => {
  const { asset, onTagUpdated, gridTileType, onThumbnailLoad } = props;

  const isDynamicGridTileType = gridTileType === GridTileType.Dynamic;

  const itemConfig = useContext(InfinityMediaItemConfigContext);

  const {
    hideTagBar,
    hideContactTagging,
    showDownloadLink,
    hideInteractionStats,
    selectMode,
  } = itemConfig;

  const handleTagUpdated = (resourceId?: string) => onTagUpdated?.(resourceId);

  if ('testimonial' in asset) {
    const respondentsUrl = selectMode
      ? undefined
      : `/projects/${asset.testimonial.uuid}/respondents/${asset.respondent
          .uuid!}`;

    return (
      <RespondentsMediaItem
        ref={detailsContainerRef}
        item={asset}
        key={asset.uuid}
        onActionSuccess={handleTagUpdated}
        onThumbnailLoad={onThumbnailLoad}
        redirectUrl={respondentsUrl}
        hideTagBar={hideTagBar}
        listItemClassName={cx({
          '!h-full !w-full': isDynamicGridTileType,
        })}
      />
    );
  }

  if ('url' in asset && Object.values(SocialType).includes(asset.type)) {
    return (
      <WebAssetThumbnail
        ref={detailsContainerRef}
        asset={asset}
        onTagUpdated={handleTagUpdated}
        onAssetLoad={onThumbnailLoad}
        hideTagBar={hideTagBar}
        hideContactTagging={hideContactTagging}
      />
    );
  }

  if ('content_rich' in asset && 'video' in asset) {
    return (
      <UserAssetListItem
        ref={detailsContainerRef}
        key={asset.uuid}
        userAsset={asset}
        onTagUpdated={() => handleTagUpdated(asset.uuid)}
        hideTagBar={hideTagBar}
        hideContactTagging={hideContactTagging}
        hideInteractionStats={hideInteractionStats}
        showDownloadLink={showDownloadLink}
        onThumbnailLoad={onThumbnailLoad}
      />
    );
  }

  return (
    <RecordingListItem
      ref={detailsContainerRef}
      key={asset.uuid}
      recording={asset as Recording}
      onTagUpdated={() => handleTagUpdated(asset.uuid)}
      hideTagBar={hideTagBar}
      hideContactTagging={hideContactTagging}
      hideInteractionStats={hideInteractionStats}
      showDownloadLink={showDownloadLink}
      onThumbnailLoad={onThumbnailLoad}
      cardClassName={cx({
        '!h-full': isDynamicGridTileType,
      })}
      imageClassName={cx({
        '!h-full': isDynamicGridTileType,
      })}
    />
  );
});

export default InfiniteMediaLibraryItem;
